import classNames from 'classnames'
import * as keycodes from 'keycode'
import { isEqual } from 'lodash'
import * as React from 'react'
import { ChangeEvent } from 'react'

import { Grid, GridGap } from 'components/Grid'

import './CodeEntry.scss'

type Code = Array<number | string | null>
const numbers = '01234567890'
const capital = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
const AllowedValues: Code = numbers
  .split('')
  .concat(capital.split(''))
  .concat([' '])

export enum CodeEntryType {
  blank = 'blank',
  pattern = 'pattern',
  shadow = 'shadow',
}

export interface CodeEntryProps {
  type: CodeEntryType
  defaultCode?: string
  numberOfCircles?: number
  headerText?: string | JSX.Element
  cards?: JSX.Element | JSX.Element[]
  footer?: string | JSX.Element
  colGap?: GridGap[]
  negative?: boolean
  giftcard?: boolean
  onCodeChange?: (code: Code) => void
  onGo?: () => void
}

interface CodeEntryState {
  code: Code
  currentPosition: number
}

class CodeEntry extends React.Component<CodeEntryProps, CodeEntryState> {
  public static defaultProps: Partial<CodeEntryProps> = { numberOfCircles: 4 }

  constructor(props: CodeEntryProps) {
    super(props)
    this.state = {
      code: this.props.defaultCode
        ? this.props.defaultCode.split('')
        : [...Array(this.props.numberOfCircles || 0)].map(() => null),
      currentPosition: this.props.defaultCode
        ? this.props.defaultCode.length - 1
        : 0,
    }
    this.onKeyDown = this.onKeyDown.bind(this)
    this.onPaste = this.onPaste.bind(this)
    this.onCodeChange = this.onCodeChange.bind(this)
    this.onInputClick = this.onInputClick.bind(this)
  }

  public componentDidUpdate(props: CodeEntryProps, state: CodeEntryState) {
    if (
      this.props.defaultCode !== undefined &&
      props.defaultCode !== this.props.defaultCode
    ) {
      const code: Array<null | string> = [
        ...Array(this.props.numberOfCircles || 0),
      ].map(() => null)
      this.props.defaultCode.split('').forEach((digit, index) => {
        code[index] = digit
      })
      this.setState({ code }, () => {
        const currentPosition =
          this.highestPosition() >= 0 ? this.highestPosition() : 0
        this.setState({ currentPosition })
        this.syncInputWithState()
        ;(this.refs[`input-${currentPosition}`] as HTMLInputElement).focus()
      })
    } else if (!isEqual(state, this.state)) {
      ;(this.refs[
        `input-${this.state.currentPosition}`
      ] as HTMLInputElement).focus()
    }
  }

  public onKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    const keycode = event.keyCode

    if ([keycodes.codes.up, keycodes.codes.right].includes(keycode)) {
      // navigate to the right
      event.preventDefault()
      const targetPosition = Math.min(
        this.state.currentPosition + 1,
        this.nextPosition(),
      )
      this.setState({ currentPosition: targetPosition })
      return
    }

    if ([keycodes.codes.down, keycodes.codes.left].includes(keycode)) {
      // navigate to the left
      event.preventDefault()
      const targetPosition = Math.max(this.state.currentPosition - 1, 0)
      this.setState({ currentPosition: targetPosition })
      return
    }

    if (keycodes.codes.backspace === keycode) {
      // remove previous and navigate left
      event.preventDefault()
      const lastElement = this.state.code.length - 1
      let targetPosition = 0

      // If last element needs deleting
      if (
        this.state.currentPosition === lastElement &&
        this.state.code[lastElement] !== null
      ) {
        targetPosition = this.state.code.length - 1
      } else {
        targetPosition = Math.max(this.state.currentPosition - 1, 0)
      }

      this.setCodeValue(targetPosition, null)
      this.setInputValue(targetPosition, null)
      this.setState({ currentPosition: targetPosition })
    }

    if (
      [
        keycodes.codes.space,
        keycodes.codes['numpad +'],
        keycodes.codes['numpad -'],
        keycodes.codes['-'],
      ].includes(keycode)
    ) {
      // Just ignore
      event.preventDefault()
    }

    if (keycodes.codes.enter === keycode && this.props.onGo) {
      this.props.onGo()
    }
  }

  // TODO: This whole component should be refactored, code is ugly
  public onPaste(event: React.ClipboardEvent<HTMLInputElement>) {
    const value = event.clipboardData.getData('Text')
    const emptyArray = Array.apply(null, Array(this.props.numberOfCircles)).map(
      () => null,
    )
    const valueArray = value
      ? value
          .toLocaleUpperCase()
          .split('')
          .splice(0, this.props.numberOfCircles)
      : emptyArray

    const code = (valueArray as null[])
      .concat(emptyArray)
      .splice(0, this.props.numberOfCircles)
    const targetPosition = Math.min(
      value.length,
      (this.props.numberOfCircles || 4) - 1,
    )
    this.setState({ code, currentPosition: targetPosition }, () => {
      if (
        value.length === this.props.numberOfCircles &&
        this.props.onCodeChange
      ) {
        this.props.onCodeChange(code)
      }
    })
  }

  public highestPosition() {
    // returns the highest position up until which the code has been filled in
    // e.g., if the code is currently [1,2,null], the highest position is 1
    // returns -1 if no circle has beenAllowedValues filled in
    for (const position in this.state.code) {
      if (this.state.code.hasOwnProperty(position)) {
        if (
          this.state.code[this.state.code.length - 1 - Number(position)] !==
          null
        ) {
          return this.state.code.length - 1 - Number(position)
        }
      }
    }
    return -1
  }

  public setCodeValue(position: number, value: string | number | null) {
    const code = this.state.code.slice()
    code[position] = value
    this.setState({ code })
  }

  public syncInputWithState() {
    this.state.code.forEach((digit, index) => {
      this.setInputValue(index, digit ? digit.toString() : null)
    })
  }

  public setInputValue(position: number, value: string | null) {
    ;(this.refs[`input-${position}`] as HTMLInputElement).value =
      value !== null ? value : ''
  }

  public clearInput(position: number) {
    if ((this.refs[`input-${position}`] as HTMLInputElement).value !== '') {
      this.setInputValue(position, null)
    }
  }

  public nextPosition() {
    // return the next available position
    // e.g., if the code is currently [1,2,null], it returns 2
    // returns the last position if all the circles have been filled in
    return Math.min(this.highestPosition() + 1, this.state.code.length - 1)
  }

  public onCodeChange(position: number, value: string | null) {
    if (value !== null && value.length > 1) {
      // don't allow more than one digit per circle
      // (strangely, maxLength, min and max dont' seem to avoid it in all cases)
      ;(this.refs[`input-${position}`] as HTMLInputElement).value = value[0]
    }

    const code = this.state.code
    let digit: string | null = null
    if (value != null) {
      if (this.props.giftcard) {
        digit =
          AllowedValues.indexOf(value[0].toUpperCase()) !== -1
            ? value[0].toUpperCase()
            : null
      } else {
        digit = value !== null ? Number(value[0]).toString() : null
      }
    }
    code[position] = digit
    // If digit isn't allowed reset the inputValue
    this.setInputValue(position, digit ? digit.toString() : null)

    // pass code to controller
    if (this.props.onCodeChange) {
      this.props.onCodeChange(code)
    }

    // save code in the state and set cursor to next available position
    const nextPosition =
      digit !== null ? this.nextPosition() : this.state.currentPosition
    this.setState({ code, currentPosition: nextPosition })
  }

  public onInputClick(position: number) {
    // don't allow inputing digits in a circle when a previous circle hasn't been filled in yet

    if (this.state.currentPosition !== position) {
      let targetPosition: number
      if (position <= this.nextPosition()) {
        // if a circle which was filled (or the next available circle) was clicked,
        // keep the cursor there
        targetPosition = position
      } else {
        // if a circle whose position is higher than the next position was clicked,
        // move the cursor to the next available position
        targetPosition = this.nextPosition()
      }
      // also, clear that input's value
      this.clearInput(position)
      this.setState({ currentPosition: targetPosition })
    }
  }

  public renderCircles() {
    return Array.apply(null, Array(this.props.numberOfCircles)).map(
      (element: any, index: number) => {
        const varProps = this.props.giftcard
          ? {
              type: 'text',
              pattern: '[0-9]',
            }
          : {
              type: 'number',
              min: '0',
              max: '9',
              pattern: '[0-9]',
            }
        return (
          <input
            autoFocus={index === this.state.currentPosition}
            value={
              this.state.code[index] !== null
                ? (this.state.code as number[])[index].toString()
                : undefined
            }
            onClick={() => {
              this.onInputClick(index)
            }}
            onPaste={this.onPaste}
            ref={`input-${index}`}
            name="code[]"
            maxLength={1}
            onKeyDown={this.onKeyDown}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              this.onCodeChange(
                index,
                event.target.value ? event.target.value : null,
              )
            }
            key={index}
            {...varProps}
          />
        )
      },
    )
  }

  public render() {
    const nrCircles = Number(this.props.numberOfCircles || 4)
    const className = classNames(
      'srprs-codeEntry',
      `background-${this.props.type}`,
      `columns-${nrCircles}`,
      this.props.negative ? 'is-negative' : '',
    )

    return (
      <div className={className}>
        {this.props.cards ? (
          <div className="srprs-code-entry-cards">
            <ul>{this.props.cards}</ul>
          </div>
        ) : null}

        {!this.props.headerText ? (
          ''
        ) : (
          <div className="header-wrapper">
            <header>{this.props.headerText}</header>
          </div>
        )}

        <Grid
          nrCols={[nrCircles]}
          minContent
          colGap={this.props.colGap}
          className="wrapper-code"
        >
          {this.renderCircles()}
        </Grid>

        {this.props.footer ? (
          <footer>
            <i>{this.props.footer}</i>
          </footer>
        ) : null}
      </div>
    )
  }
}

export default CodeEntry
